import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '@e2n/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { ReactNode, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useCreateTermineFormContext } from '@/domain/termine/features/CreateTermin/shared/useCreateTermineFormContext';
import { UnsavedChangesDrawer } from '@/domain/termine/components';

interface CreateTermineDrawerWrapperProps {
  onClose: () => void;
  children: ReactNode;
}

export function CreateTermineDrawerWrapper({
  onClose,
  children,
}: CreateTermineDrawerWrapperProps) {
  const { t } = useTranslation();
  const { form } = useCreateTermineFormContext();
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);

  return (
    <>
      <Drawer
        repositionInputs={false}
        open
        onOpenChange={() => {
          const hasUnsavedChanges = Object.values(form.state.fieldMeta).some(
            (field) => !field?.isDefaultValue,
          );
          if (hasUnsavedChanges) setShowUnsavedChangesDialog(true);
          else onClose();
        }}
        dismissible>
        <DrawerContent
          showHandle={false}
          className={cn(
            'h-svh w-screen',
            'rounded-none border-0',
            'data-[vaul-drawer-direction=bottom]:mt-0',
            'data-[vaul-drawer-direction=bottom]:max-h-none',
            'data-[vaul-drawer-direction=bottom]:rounded-none',
            'data-[vaul-drawer-direction=bottom]:border-0',
          )}>
          <DrawerTitle className="sr-only">
            {t('domain.termine.sheet.neuerTermin')}
          </DrawerTitle>
          <DrawerDescription className="sr-only">
            {t('domain.termine.sheet.neuerTermin')}
          </DrawerDescription>
          <Suspense
            fallback={
              <div className="flex flex-col gap-4 items-center justify-center text-sm">
                <FontAwesomeIcon
                  icon={faSpinnerThird}
                  className="text-primary-blue-200"
                  spin
                  size="4x"
                />
                {t('actions.loading')}
              </div>
            }>
            {children}
          </Suspense>
        </DrawerContent>
      </Drawer>

      <UnsavedChangesDrawer
        open={showUnsavedChangesDialog}
        onCancel={() => setShowUnsavedChangesDialog(false)}
        onConfirm={() => {
          form.reset();
          setShowUnsavedChangesDialog(false);
          onClose();
        }}
      />
    </>
  );
}
